/**
 * Saved-view engine (M5.md AC-10). Pure + browser-safe: builds a `SavedView`
 * from a QUERY result and reproduces it with ZERO model calls — the match set
 * is recomputed from the deterministic filter predicate, the style overlay
 * folds through the same op-stack reducer the GUI uses, and the viewport is the
 * saved framing. M6's viewer imports these to render a promoted view on the
 * public/embed map (the round-trip guaranteed by the key invariant: persisted
 * state is structured ops + views, never the prompt or an LLM response).
 */

import type { FilterNode, MapOp, SavedView } from "@/lib/contracts/nl";
import type { MapSpec } from "@/lib/map/mapspec";
import type { Point } from "@/lib/map/pointset";
import { selectPoints } from "./filter";
import { reduceStack } from "./opstack";

export interface BuildSavedViewParams {
  /** The NL prompt that produced the query (the view's label). */
  sourceQuestion: string;
  /** Deterministic attribute predicate from the executable query, if any. */
  filterPredicate?: FilterNode | null;
  /** NL style ops layered when the view renders (usually the op stack in effect). */
  styleOverlay?: MapOp[];
  /** Saved framing; null → fit to the matched set. */
  viewport?: { center: [number, number]; zoom: number } | null;
  /** Injectable for deterministic tests. */
  id?: string;
  now?: () => string;
}

/** Build a persistable SavedView from a promoted QUERY result. */
export function buildSavedView(p: BuildSavedViewParams): SavedView {
  const createdAt = p.now?.() ?? new Date().toISOString();
  return {
    id: p.id ?? globalThis.crypto?.randomUUID?.() ?? `view_${createdAt}`,
    sourceQuestion: p.sourceQuestion,
    filterPredicate: p.filterPredicate ?? null,
    styleOverlay: p.styleOverlay ?? [],
    viewport: p.viewport ?? null,
    createdAt,
  };
}

/**
 * Recompute the view's matched row ids over the (local) points — deterministic,
 * LLM-free. With no predicate (spatial/top-N views) the framing is the view, so
 * every point is "in view" and the viewport does the work.
 */
export function savedViewMatchIds(view: SavedView, points: Point[]): string[] {
  const matched = view.filterPredicate ? selectPoints(points, view.filterPredicate) : points;
  return matched.map((pt) => pt.id);
}

/** The rendered spec for a saved view = the published spec with its style overlay folded on. */
export function savedViewSpec(base: MapSpec, view: SavedView): MapSpec {
  return view.styleOverlay.length > 0 ? reduceStack(base, view.styleOverlay) : base;
}
